import { CustomerAssignedVoucher } from 'src/app/stores/stores';
import { getQueryParamFromUrl } from './UrlQueryUtils';

export function normalizeVoucherCode(
  voucherCode: string | null | undefined,
): string | null {
  const trimmedVoucherCode = (voucherCode || '').trim();
  if (!trimmedVoucherCode) {
    return null;
  }

  return trimmedVoucherCode.toUpperCase();
}

export interface FormatVoucherValueOptions {
  currencyIsoCode?: string;
  currencySymbol?: string;
}

export function formatVoucherValue(
  voucher: CustomerAssignedVoucher,
  options: FormatVoucherValueOptions = {},
): string {
  if (voucher.formattedDiscount) {
    return voucher.formattedDiscount;
  }

  if (voucher.discountType === 'PERCENTILE') {
    return `${voucher.discount}%`;
  }

  const { currencyIsoCode, currencySymbol } = options;
  if (currencySymbol) {
    return `${currencySymbol}${voucher.discount}`;
  }
  if (currencyIsoCode) {
    return `${voucher.discount} ${currencyIsoCode}`.trim();
  }
  return `${voucher.discount}`;
}

export function isVoucherUnavailable(
  voucher: CustomerAssignedVoucher,
): boolean {
  const endDate = voucher.endDate ? new Date(voucher.endDate) : null;
  return (
    !voucher.isActive ||
    voucher.discount === 0 ||
    voucher.isConsumed ||
    (!!endDate && endDate < new Date())
  );
}

/**
 * Search string first, then a query embedded in the hash — the lookup order now
 * lives in `getQueryParamFromUrl`, shared with the Sales Point reader (#11426)
 * so the two cannot drift apart.
 *
 * The delegation is behaviour-preserving: the old code fell through to the hash
 * when `normalizeVoucherCode(searchValue)` was falsy, and `normalizeVoucherCode`
 * above rejects exactly one thing — a value that is blank after trimming — which
 * is the same condition `readParam` in `getQueryParamFromUrl` falls through on.
 * Uppercasing after the lookup is equivalent because it cannot turn a non-blank
 * value into a blank one. Keep that true if `normalizeVoucherCode` ever grows a
 * charset or length rule: it would then reject non-blank input the shared reader
 * still accepts, and `?voucher=<rejected>#l/1?voucher=<valid>` would start
 * returning `null` instead of the hash code. The spec pins the fallthrough.
 */
export function getVoucherCodeFromUrl(
  search: string,
  hash: string = '',
): string | null {
  return normalizeVoucherCode(getQueryParamFromUrl(search, hash, 'voucher'));
}
